import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subscription, of, throwError, timer } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

import { ApiResponse } from "src/app/core/models/api.model";
import { AuthService } from "./auth.service";
import { NotificationApiService, PublishNotificationInput } from "./notification-api.service";
import { NotificationSocketService } from "./notification-socket.service";
import { userHasNotificationCenterAccess } from "src/app/core/models/user.model";
import {
  AppNotification,
  NotificationPage,
  NotificationUnreadCount,
} from "src/app/core/models/notification.model";

const PAGE_SIZE = 20;
const UNREAD_COUNT_POLL_MS = 60_000;

export interface NotificationListFilter {
  unreadOnly?: boolean;
  readOnly?: boolean;
}

/**
 * Injectable client for the in-app notification center (issue #416).
 *
 * Deliberately NOT named `NotificationService` — that name is already the
 * app's toast wrapper (`core-services/services/notification.service.ts`,
 * `VngToastService`), a completely unrelated, transient, non-persistent
 * concept. Keeping the names distinct was an explicit issue requirement.
 *
 * MANDATORY FEATURE-MAP GATE (issue #416, updated for #559): every public
 * method that would touch the network — {@link init}, {@link loadFirstPage},
 * {@link loadMore}, {@link markRead}, {@link markAllRead}, {@link dismiss},
 * {@link respond}, {@link publish} — is a no-op unless {@link isEligible} is
 * true, i.e.
 * `userHasNotificationCenterAccess(authService.userDetails?.user_details)`
 * (today: the per-user feature map's `in_app_notifications` key, sourced
 * from `GET v2/meta-data`). This check happens INSIDE the service, not only
 * at the call site, so this stays inert even if some future code injects
 * `AppNotificationService` without going through the gated
 * `NotificationBellComponent` (belt-and-suspenders: the bell is also
 * `*ngIf`-gated out of the DOM entirely for ineligible users in
 * `header.component.html`, so in practice the service is never even
 * constructed-and-used for them).
 *
 * Resilience: flymsg-node#15 (the backend half) may not be deployed, may
 * 404, or its Socket.IO namespace may be unreachable (see
 * `NotificationSocketService`'s JSDoc for the known missing-scope issue).
 * Every REST call here degrades to an empty/unchanged state on failure —
 * never throws out of a passive load, never spams the console, never
 * blocks the app shell. The two exceptions are the explicit optimistic
 * *actions* (`markRead`/`markAllRead`/`dismiss`/`respond`), which roll back
 * their local state on failure so the UI doesn't lie about server state,
 * and `publish()`, which is a caller-invoked integration point and
 * surfaces failure like any other API call a feature explicitly awaits.
 */
@Injectable({ providedIn: "root" })
export class AppNotificationService {
  private notificationsSubject = new BehaviorSubject<AppNotification[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  notifications$ = this.notificationsSubject.asObservable();
  unreadCount$ = this.unreadCountSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();

  private initialized = false;
  private page = 1;
  private hasMore = true;
  private loadingMore = false;
  private activeFilter: NotificationListFilter = {};
  /** Increments whenever the selected tab changes, invalidating older list responses. */
  private listRequestVersion = 0;
  private subs = new Subscription();
  private pollingSub: Subscription | null = null;

  constructor(
    private readonly authService: AuthService,
    private readonly api: NotificationApiService,
    private readonly socket: NotificationSocketService
  ) {}

  /** Whether the signed-in user may use the notification center at all (see class JSDoc). */
  get isEligible(): boolean {
    return (
      this.authService.isLoggedIn() && userHasNotificationCenterAccess(this.authService.userDetails?.user_details)
    );
  }

  get hasMoreToLoad(): boolean {
    return this.hasMore;
  }

  /**
   * Starts the notification center for an eligible user: loads the first
   * page, connects the realtime socket, and arms the unread-count polling
   * fallback. Idempotent — safe to call from every `NotificationBellComponent
   * .ngOnInit()`. No-op (no network calls at all) when {@link isEligible}
   * is false.
   */
  init(): void {
    if (!this.isEligible || this.initialized) {
      return;
    }
    this.initialized = true;

    this.loadFirstPage({ unreadOnly: true });
    this.refreshUnreadCount();

    this.subs.add(
      this.socket.notificationNew$.subscribe((notification) => this.onNotificationPushed(notification))
    );
    this.subs.add(this.socket.unreadCount$.subscribe((count) => this.unreadCountSubject.next(count)));
    this.subs.add(
      this.socket.connected$.subscribe((connected) => {
        if (connected) {
          this.stopPolling();
        } else {
          this.startPolling();
        }
      })
    );

    this.socket.connect();
  }

  /** Tears the notification center down (bell/logout teardown). Safe to call unconditionally. */
  teardown(): void {
    this.socket.disconnect();
    this.stopPolling();
    this.subs.unsubscribe();
    this.subs = new Subscription();
    this.initialized = false;
    this.page = 1;
    this.hasMore = true;
    this.listRequestVersion++;
    this.loadingMore = false;
    this.activeFilter = {};
    this.notificationsSubject.next([]);
    this.unreadCountSubject.next(0);
    this.loadingSubject.next(false);
  }

  /** Loads page 1 for the selected read state. */
  loadFirstPage(filter: NotificationListFilter = {}): void {
    if (!this.isEligible) {
      return;
    }
    const requestedFilter = { ...filter };
    const requestVersion = ++this.listRequestVersion;
    this.activeFilter = requestedFilter;
    this.page = 1;
    this.hasMore = true;
    this.loadingMore = false;
    this.loadingSubject.next(true);

    this.api
      .list({ unreadOnly: requestedFilter.unreadOnly, page: this.page, limit: PAGE_SIZE })
      .pipe(catchError(() => this.emptyPage()))
      .subscribe((response) => {
        if (requestVersion !== this.listRequestVersion) {
          return;
        }
        const responseData = response?.result?.data ?? [];
        const data = this.filterForTab(responseData, requestedFilter);
        this.notificationsSubject.next(data);
        this.hasMore = responseData.length >= PAGE_SIZE;
        if (this.shouldAdvanceReadOnlyPage(requestedFilter)) {
          this.loadMore();
          return;
        }
        this.loadingSubject.next(false);
      });
  }

  /** Appends the next page for infinite scroll. No-op while already loading or exhausted. */
  loadMore(): void {
    if (!this.isEligible || !this.hasMore || this.loadingMore) {
      return;
    }
    this.loadingMore = true;
    const nextPage = this.page + 1;
    const requestVersion = this.listRequestVersion;
    const requestedFilter = { ...this.activeFilter };

    this.api
      .list({ unreadOnly: requestedFilter.unreadOnly, page: nextPage, limit: PAGE_SIZE })
      .pipe(catchError(() => this.emptyPage()))
      .subscribe((response) => {
        if (requestVersion !== this.listRequestVersion) {
          return;
        }
        const responseData = response?.result?.data ?? [];
        const data = this.filterForTab(responseData, requestedFilter);
        this.page = nextPage;
        this.hasMore = responseData.length >= PAGE_SIZE;
        this.loadingMore = false;
        this.notificationsSubject.next([...this.notificationsSubject.value, ...data]);
        if (this.shouldAdvanceReadOnlyPage(requestedFilter)) {
          this.loadMore();
          return;
        }
        this.loadingSubject.next(false);
      });
  }

  /** Marks a single notification read (optimistic, rolls back on failure). */
  markRead(id: string): void {
    if (!this.isEligible) {
      return;
    }
    const current = this.notificationsSubject.value;
    const target = current.find((n) => n.id === id);
    if (!target || target.read_at) {
      return;
    }

    const previous = current;
    const previousUnread = this.unreadCountSubject.value;
    this.notificationsSubject.next(this.withField(current, id, { read_at: this.nowSeconds() }));
    this.unreadCountSubject.next(Math.max(0, previousUnread - 1));

    this.api.markRead(id).subscribe({
      error: () => {
        this.notificationsSubject.next(previous);
        this.unreadCountSubject.next(previousUnread);
      },
    });
  }

  /** Marks every currently-loaded notification read (optimistic, rolls back on failure). */
  markAllRead(): void {
    if (!this.isEligible) {
      return;
    }
    const previous = this.notificationsSubject.value;
    const previousUnread = this.unreadCountSubject.value;
    const now = this.nowSeconds();
    this.notificationsSubject.next(previous.map((n) => (n.read_at ? n : { ...n, read_at: now })));
    this.unreadCountSubject.next(0);

    this.api.markAllRead().subscribe({
      error: () => {
        this.notificationsSubject.next(previous);
        this.unreadCountSubject.next(previousUnread);
      },
    });
  }

  /** Removes a notification from the list (optimistic, rolls back on failure). */
  dismiss(id: string): void {
    if (!this.isEligible) {
      return;
    }
    const previous = this.notificationsSubject.value;
    const index = previous.findIndex((n) => n.id === id);
    if (index === -1) {
      return;
    }
    const target = previous[index];
    const previousUnread = this.unreadCountSubject.value;
    this.notificationsSubject.next(previous.filter((n) => n.id !== id));
    if (!target.read_at) {
      this.unreadCountSubject.next(Math.max(0, previousUnread - 1));
    }

    this.api.dismiss(id).subscribe({
      error: () => {
        this.notificationsSubject.next(previous);
        this.unreadCountSubject.next(previousUnread);
      },
    });
  }

  /** Submits a response payload for an `actions`/`survey` notification (optimistic, rolls back on failure). */
  respond(id: string, payload: Record<string, unknown>): void {
    if (!this.isEligible) {
      return;
    }
    const previous = this.notificationsSubject.value;
    const target = previous.find((n) => n.id === id);
    if (!target) {
      return;
    }
    const previousUnread = this.unreadCountSubject.value;
    const now = this.nowSeconds();
    this.notificationsSubject.next(
      this.withField(previous, id, { response: payload, read_at: target.read_at ?? now })
    );
    if (!target.read_at) {
      this.unreadCountSubject.next(Math.max(0, previousUnread - 1));
    }

    this.api.respond(id, payload).subscribe({
      error: () => {
        this.notificationsSubject.next(previous);
        this.unreadCountSubject.next(previousUnread);
      },
    });
  }

  /**
   * Publishes a user-action-originated notification (e.g. a FlyShare share
   * event) — see `NotificationApiService.publish`'s JSDoc: the backend does
   * not yet expose the user-scoped trigger endpoint this calls, so today
   * every call fails (404) until a follow-up lands. Deliberately NOT
   * swallowed: unlike the passive loads above, this is a caller-invoked
   * action a feature explicitly awaits, so it should see the real failure
   * rather than a false success. Gated the same as every other method here
   * (returns immediately if not `isEligible`) to keep "zero network calls
   * for non-beta users" true for the write side too.
   */
  publish(input: PublishNotificationInput): Observable<AppNotification> {
    if (!this.isEligible) {
      return throwError(() => new Error("Notification center is not available for this user."));
    }
    return this.api.publish(input).pipe(
      switchMap((response) => {
        const data = response?.result?.data;
        return data ? of(data) : throwError(() => new Error("Publish notification returned no payload."));
      })
    );
  }

  private onNotificationPushed(notification: AppNotification): void {
    const current = this.notificationsSubject.value;
    if (current.some((n) => n.id === notification.id)) {
      return;
    }
    if (this.activeFilter.unreadOnly && notification.read_at) {
      return;
    }
    if (this.activeFilter.readOnly && !notification.read_at) {
      return;
    }
    this.notificationsSubject.next([notification, ...current]);
  }

  private startPolling(): void {
    if (this.pollingSub || !this.isEligible) {
      return;
    }
    this.pollingSub = timer(UNREAD_COUNT_POLL_MS, UNREAD_COUNT_POLL_MS)
      .pipe(switchMap(() => this.refreshUnreadCount$()))
      .subscribe();
  }

  private stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = null;
  }

  private refreshUnreadCount(): void {
    this.refreshUnreadCount$().subscribe();
  }

  private refreshUnreadCount$(): Observable<ApiResponse<NotificationUnreadCount>> {
    return this.api.unreadCount().pipe(
      catchError(() => this.emptyUnreadCount$()),
      map((response) => {
        if (typeof response?.result?.count === "number") {
          this.unreadCountSubject.next(response.result.count);
        }
        return response;
      })
    );
  }

  private withField(
    list: AppNotification[],
    id: string,
    patch: Partial<AppNotification>
  ): AppNotification[] {
    return list.map((n) => (n.id === id ? { ...n, ...patch } : n));
  }

  /**
   * flymsg-node currently supports only `unread_only`; until its companion
   * `read_only` filter lands, the Read tab filters the received page locally.
   */
  private filterForTab(
    notifications: AppNotification[],
    filter: NotificationListFilter
  ): AppNotification[] {
    if (filter.readOnly) {
      return notifications.filter((notification) => Boolean(notification.read_at));
    }
    return notifications;
  }

  /**
   * A client-filtered Read list needs enough visible rows for its infinite
   * scroll to be reachable. Keep inspecting full source pages until the
   * filtered list reaches one page or the source is exhausted.
   */
  private shouldAdvanceReadOnlyPage(filter: NotificationListFilter): boolean {
    return (
      filter.readOnly === true &&
      this.hasMore &&
      this.notificationsSubject.value.length < PAGE_SIZE
    );
  }

  private nowSeconds(): number {
    return Math.floor(Date.now() / 1000);
  }

  /** Degrade-to-empty for a failed list/loadMore call — never throws, just yields nothing new. */
  private emptyPage(): Observable<ApiResponse<NotificationPage>> {
    return of({ result: { data: [], meta: { total: 0, page: this.page, limit: PAGE_SIZE } } });
  }

  /** Degrade-to-unchanged for a failed unread-count poll — keeps the last known-good value. */
  private emptyUnreadCount$(): Observable<ApiResponse<NotificationUnreadCount>> {
    return of({ result: { count: this.unreadCountSubject.value } });
  }
}
